import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { SupportService } from 'src/app/support.service';
import {
  Client,
  ClientProfileReportMonth,
  ClientProfileReportResponse,
  ClientProfileReportSummary
} from 'src/typings';
import { buildClientProfitChart } from './client-profit-chart';
import { ClientService } from '../../client.service';

const EMPTY_SUMMARY: ClientProfileReportSummary = {
  avg_markup_percent: 0,
  avg_orders_per_month: 0,
  avg_profit_per_month: 0,
  current_profit: 0,
  current_orders_count: 0,
  current_orders_debt: 0,
  current_prepayment_balance: 0,
  overdue_debt: 0
};

@Component({
  selector: 'app-client-reports',
  templateUrl: './client-reports.component.html',
  styleUrls: ['./client-reports.component.scss']
})
export class ClientReportsComponent implements OnChanges, OnDestroy {
  @Input() client?: Client;

  monthsCount = 3;

  chart = buildClientProfitChart([]);

  loading = false;

  errorText = '';

  report: ClientProfileReportResponse | null = null;

  private requestId = 0;

  constructor(
    private readonly clientService: ClientService,
    private readonly notificationService: NotificationService,
    public readonly supportService: SupportService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.client) return;

    const clientId = Number(changes.client.currentValue?.id || 0);
    if (!clientId) {
      ++this.requestId;
      this.loading = false;
      this.report = null;
      this.errorText = '';
      return;
    }

    void this.loadReport(clientId);
  }

  ngOnDestroy(): void {
    ++this.requestId;
  }

  setPeriod(months: number): void {
    if (![3, 6, 12].includes(months) || this.monthsCount === months) return;
    this.monthsCount = months;
    this.reload();
  }

  reload(): void {
    if (this.client?.id) void this.loadReport(Number(this.client.id));
  }

  chartTooltip(month: ClientProfileReportMonth): string {
    return `${month.month_label}${month.is_current ? ' (неполный месяц)' : ''}
Прибыль: ${this.supportService.formatMoney(month.profit)}
Оборот: ${this.supportService.formatMoney(month.turnover)}`;
  }

  get months(): ClientProfileReportMonth[] {
    return this.report?.months || [];
  }

  get summary(): ClientProfileReportSummary {
    return this.report?.summary || EMPTY_SUMMARY;
  }

  trackByMonth(_: number, month: ClientProfileReportMonth) {
    return month.month_key;
  }

  private async loadReport(clientId: number) {
    const requestId = ++this.requestId;
    this.loading = true;
    this.errorText = '';

    try {
      const report = await firstValueFrom(this.clientService.getProfileReport(clientId, this.monthsCount));
      if (requestId !== this.requestId) return;
      this.report = report;
      this.chart = buildClientProfitChart(report.months);
    } catch (error: unknown) {
      if (requestId !== this.requestId) return;
      const message = extractApiErrorMessage(error, 'Не удалось загрузить отчет клиента');
      this.errorText = message;
      this.report = null;
      this.notificationService.showError(message);
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
      }
    }
  }
}
